import { HStack, Input, Progress, VStack } from '@chakra-ui/react';
import { Icon } from '@jurnee/common/src/components/Icon';
import { ContentType } from '@jurnee/common/src/constants/ContentTypes';
import { Label } from './Label';
import { SubLabel } from './SubLabel';
import { useFileInput } from './useFileInput';

interface Props {
  label: string;
  sublabel: string;
  contentTypes: ContentType[];
  defaultName?: string;
  defaultSize?: number;
  defaultIdentifier?: number;
  imageMaxWidth?: number;
  labelMaxWidth?: number;
  isSavedFile?: boolean;
  isLoading?: boolean;
  onChange?(file: File): void;
  onDownload?(identifier: number): void;
  onRemove?(identifier: number): void;
}

export function FileInput(props: Props) {
  const { onDownload } = props;
  const {
    name, size, identifier, dragActive, hasFile, fileInput,
    handleClick, handleChange, handleDrop, handleDrag, handleRemove
  } = useFileInput(props);

  const editProps = props.onChange ? {
    borderColor: dragActive ? 'gray.300' : 'gray.200',
    _hover: { borderColor:'gray.300', cursor: 'pointer' },
    _active: { borderColor:'gray.400', cursor: 'pointer' }
  } : {};

  return (
    <VStack
      w="100%"
      bgColor="white"
      border={props.isSavedFile ? '1px solid' : '1px dashed'}
      borderColor="gray.200"
      borderRadius={4}
      spacing={0}
      overflow="hidden"
      {...editProps}
    >
      <HStack
        w="100%"
        p={3}
        spacing={2}
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <HStack w="100%" justifyContent="space-between">
          <VStack spacing={1} alignItems="flex-start">
            <Label maxW={props.labelMaxWidth || 260} label={props.label} name={name} isSavedFile={props.isSavedFile} />
            <SubLabel sublabel={props.sublabel} size={size} />
          </VStack>

          {
            hasFile ? (
              <HStack spacing={2}>
                {
                  onDownload && identifier !== undefined &&
                  <Icon
                    icon="download"
                    size={5}
                    _hover={{ color: 'gray.600', cursor: 'pointer' }}
                    onClick={() => onDownload(identifier)}
                  />
                }
                {
                  props.onRemove &&
                  <Icon
                    icon="squareRoundedX"
                    size={5}
                    _hover={{ color: 'gray.600', cursor: 'pointer' }}
                    onClick={handleRemove}
                  />
                }
              </HStack>
            ) : (
              <Icon icon="fileUpload" size={5} />
            )
          }
        </HStack>

        <Input
          key={name}
          type="file"
          accept={props.contentTypes.join(', ')}
          onChange={handleChange}
          ref={fileInput}
          isDisabled={!props.onChange}
          hidden
        />
      </HStack>

      {
        props.isLoading &&
          <Progress
            w="100%"
            mt="-4px"
            size="xs"
            colorScheme="gray"
            isIndeterminate
          />
      }
    </VStack>
  );
}